import { localStorageService } from '@widgets/shared/services/localStorageService'
import { mergeWith } from 'lodash-es'
import { computed, onBeforeUnmount, ref, type Ref } from '@/composition/vue/compositionApi'
import type { DeepPartial } from '@/types'

export const useLocalStorageValue = <T extends string | number | object>(key: string, defaultValue: T) => {
  const getRealValue = () => {
    return localStorageService.get<T>(key)
  }

  const realValue = ref(getRealValue()) as Ref<T | undefined>

  const get = () => {
    return getRealValue() ?? defaultValue
  }

  const set = (val: T) => {
    localStorageService.set(key, val)
  }

  const patch = (val: DeepPartial<T>) => {
    const newValue = mergeWith(getRealValue(), val, (objValue, srcValue, k, obj) => {
      // Delete values that are set to undefined
      if (objValue !== srcValue && typeof srcValue === 'undefined') {
        obj[k] = srcValue
      }
      // Don't merge arrays, we need replace it
      return Array.isArray(srcValue) ? srcValue : undefined
    })
    localStorageService.set(key, newValue)
  }

  const remove = () => {
    localStorageService.remove(key)
  }

  const listenerId = localStorageService.watch<T>(key, (val) => {
    realValue.value = val
  })

  onBeforeUnmount(() => {
    localStorageService.unwatch(listenerId)
  })

  return {
    value: computed(() => realValue.value ?? defaultValue),
    get,
    set,
    patch,
    remove,
  }
}
